import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import range from "lodash/range";
import Box from "@ui-kit/layout/Box/Box.web";
import Select, { SelectProps } from "@ui-kit/form/Select/Select.web";

export interface QuantitySelectProps {
  id: SelectProps<number>["id"];
  minSelectionAmount?: number;
  maxSelectionAmount?: number;
  amountSelected: number;
  onAmountSelectedChange?: (amount: number) => void;
  isQuantityLoading?: boolean;
  isCalendar?: boolean;
  isModal?: boolean;
}

const QuantitySelect: React.FC<QuantitySelectProps> = ({
  id,
  minSelectionAmount = 0,
  maxSelectionAmount = 0,
  amountSelected,
  onAmountSelectedChange = undefined,
  isQuantityLoading = false,
  isCalendar = false,
  isModal = false,
}) => {
  const { t } = useTranslation("purchases");

  const availQuantities = useMemo(
    () =>
      range(minSelectionAmount, maxSelectionAmount + 1).map((num) => ({
        id: num,
        name: `${num}`,
      })),
    [minSelectionAmount, maxSelectionAmount]
  );

  return (
    <Box
      width={{
        base: "100%",
        md: isCalendar ? "100%" : "160px",
        lg: "element.9xl",
      }}
      position={isModal ? "relative" : "inherit"}
    >
      <Select
        id={id}
        disabled={isQuantityLoading}
        isLoading={isQuantityLoading}
        ariaLabel={t("Select a quantity")}
        variant="secondary"
        items={
          minSelectionAmount
            ? [{ id: 0, name: "0" }, ...availQuantities] // if there is a minimum purchase limit, should be able to select 0
            : availQuantities
        }
        selectedItem={{ id: amountSelected, name: `${amountSelected}` }}
        onSelectedItemChange={({ selectedItem }) => {
          if (selectedItem) {
            // Where `id` is the number in the generated array from minSelectionAmount and maxSelectionAmount
            onAmountSelectedChange?.(selectedItem.id);
          }
        }}
        componentProps={{
          root: {
            position: "relative",
          },
        }}
      />
    </Box>
  );
};

export default QuantitySelect;
